import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { productsAPI } from '../services/api';

const parse = (field, raw) => {
  const n = field === 'price' ? parseFloat(String(raw).replace(',', '.')) : parseInt(raw, 10);
  return Number.isNaN(n) || n < 0 ? null : n;
};

/** Click-to-edit number (price or stock) inside the desktop products table : Enter/blur saves, Escape cancels. */
const InlineQuickEdit = ({ product, field, onSaved, className = '', children }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const escaped = useRef(false);

  useEffect(() => { if (editing) { ref.current?.focus(); ref.current?.select(); } }, [editing]);

  const open = (e) => { e.stopPropagation(); setValue(field === 'price' ? product.price.toFixed(2) : String(product.stock ?? 0)); escaped.current = false; setEditing(true); };
  const cancel = () => { escaped.current = true; setEditing(false); };
  const save = async () => {
    if (escaped.current || saving) return;
    const n = parse(field, value);
    if (n === null) { toast.error(field === 'price' ? 'Prix invalide' : 'Stock invalide'); return; }
    const current = field === 'price' ? product.price : (product.stock ?? 0);
    if (n === current) { setEditing(false); return; }
    setSaving(true);
    try {
      const updated = await productsAPI.quickEdit(product.id, { [field]: n });
      toast.success(`${product.name} : ${field === 'price' ? `${current.toFixed(2)} € → ${n.toFixed(2)} €` : `stock ${current} → ${n}`}`);
      setEditing(false);
      onSaved?.(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally { setSaving(false); }
  };
  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } else if (e.key === 'Escape') { e.preventDefault(); cancel(); } };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={`inline-edit-${field}-${product.id}`}>
        <input
          ref={ref}
          type="number"
          step={field === 'price' ? '0.01' : '1'}
          min="0"
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          onBlur={save}
          data-testid={`inline-edit-${field}-input-${product.id}`}
          className="w-24 border border-orange-400 rounded px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-300"
        />
        {saving ? <Loader2 size={14} className="animate-spin text-orange-500" /> : <span className="text-xs text-gray-400">{field === 'price' ? '€' : 'u.'}</span>}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={open}
      title={field === 'price' ? 'Modifier le prix' : 'Modifier le stock'}
      data-testid={`inline-edit-${field}-${product.id}`}
      className={`group inline-flex items-center gap-1 rounded px-1 -mx-1 hover:bg-orange-50 text-left ${className}`}
    >
      {children}
      <Pencil size={11} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

export default InlineQuickEdit;
